/**
 * ImpersonationBanner — persistent yellow strip shown when the current
 * session token contains an `impersonated_by` claim. Only the platform
 * owner can ever produce such a token (via the Access Control Center).
 *
 * The banner blocks no actions; it is purely a visual safety net.
 * Clicking "Stop Impersonating" calls /api/access-control/impersonate-stop
 * (audited) and restores the owner's original token from local backup.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldAlert, LogOut, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImpersonationBanner = () => {
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const token = localStorage.getItem('admin_token');
      if (!token) return;
      try {
        const r = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (r.data?.impersonated_by) setInfo(r.data);
      } catch { /* offline */ }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const stop = async () => {
    try {
      await axios.post(`${API_URL}/api/access-control/impersonate-stop`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` },
      });
    } catch { /* audited best-effort */ }
    const backup = localStorage.getItem('admin_token_owner_backup');
    const backupUser = localStorage.getItem('admin_user_owner_backup');
    if (backup) {
      localStorage.setItem('admin_token', backup);
      localStorage.removeItem('admin_token_owner_backup');
    }
    if (backupUser) {
      localStorage.setItem('admin_user', backupUser);
      localStorage.removeItem('admin_user_owner_backup');
    }
    navigate('/admin/access-control');
    setTimeout(() => window.location.reload(), 80);
  };

  if (!info) return null;
  return (
    <div data-testid="impersonation-banner"
      className="sticky top-0 z-50 bg-gradient-to-r from-amber-500 via-amber-600 to-orange-600
        text-white px-4 py-2 flex items-center justify-between shadow-md">
      <div className="flex items-center gap-2 text-sm">
        <ShieldAlert className="w-4 h-4" />
        <span className="font-bold uppercase tracking-wider text-[11px]">
          Owner Impersonation Mode
        </span>
        <span className="text-white/90">
          You are viewing as <b>{info.full_name || info.email}</b> ({info.role})
        </span>
        <span className="hidden md:inline-flex items-center gap-1 text-[11px] text-white/70 ml-3">
          <Clock className="w-3 h-3" /> Started by {info.impersonated_by}
        </span>
      </div>
      <button onClick={stop}
        data-testid="impersonation-stop-btn"
        className="flex items-center gap-1 px-3 py-1 bg-white/15 hover:bg-white/25
          rounded text-xs font-semibold transition-colors">
        <LogOut className="w-3 h-3" /> Stop Impersonating
      </button>
    </div>
  );
};

export default ImpersonationBanner;
